import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, IconUserGroup } from '../../../base/icons';
import { Switch } from '../../../base/react';
import { toggleAutoPromoteVisitors, toggleVisitorsRaiseHandDisabled } from '../../../visitors/actions';
import { useVisitorsToggle } from '../../../visitors/components/web/useVisitorsToggle';
import { getAutoPromoteToggleError, getAutoPromoteToggleValue, getRaiseHandDisabledToggleError, getRaiseHandDisabledToggleValue, isAutoPromoteTogglePending, isRaiseHandDisabledTogglePending } from '../../../visitors/functions';
import { useModerationStyles } from './styles';
/**
 * The moderator-only visitor mode card of the moderation pane. Turning it on
 * opens the apply-mode dialog (same flow as the toolbar entry); while it is on,
 * a secondary switch controls automatic admission of visitor join requests
 * (room metadata {@code visitors.autoPromote}).
 *
 * @returns {JSX.Element | null}
 */
const VisitorsSection = () => {
    const { t } = useTranslation();
    const { classes, cx } = useModerationStyles();
    const dispatch = useDispatch();
    const { visible, toggleError, toggleValue, togglePending, onToggleVisitors, requests } = useVisitorsToggle();
    const autoPromoteValue = useSelector(getAutoPromoteToggleValue);
    const autoPromotePending = useSelector(isAutoPromoteTogglePending);
    const autoPromoteError = useSelector(getAutoPromoteToggleError);
    const raiseHandDisabledValue = useSelector(getRaiseHandDisabledToggleValue);
    const raiseHandDisabledPending = useSelector(isRaiseHandDisabledTogglePending);
    const raiseHandDisabledError = useSelector(getRaiseHandDisabledToggleError);
    const onToggleAutoPromote = useCallback((value) => {
        if (autoPromotePending) {
            return;
        }
        dispatch(toggleAutoPromoteVisitors(value));
    }, [dispatch, autoPromotePending]);
    const onToggleRaiseHandDisabled = useCallback((value) => {
        if (raiseHandDisabledPending) {
            return;
        }
        dispatch(toggleVisitorsRaiseHandDisabled(value));
    }, [dispatch, raiseHandDisabledPending]);
    if (!visible) {
        return null;
    }
    return (React.createElement("div", { className: classes.card },
        React.createElement("div", { className: classes.cardHeader },
            React.createElement("div", { className: classes.cardIcon },
                React.createElement(Icon, { size: 16, src: IconUserGroup })),
            React.createElement("span", { className: classes.cardTitle }, t('visitors.section.title'))),
        React.createElement("p", { className: classes.cardDescription }, t('visitors.section.description')),
        React.createElement("div", { className: cx(classes.row, classes.rowDivided) },
            React.createElement("div", { className: classes.rowText },
                React.createElement("label", { className: classes.rowLabel, htmlFor: 'visitors-pane-switch' }, t('visitors.section.toggleLabel')),
                requests.length > 0 && (React.createElement("span", { className: cx(classes.notice, classes.noticeWarning) }, t('visitors.section.requestsPending', { count: requests.length }))),
                toggleError && (React.createElement("span", { className: cx(classes.notice, classes.noticeError) }, t(toggleError)))),
            React.createElement(Switch, { disabled: togglePending, id: 'visitors-pane-switch', onValueChange: onToggleVisitors, value: toggleValue })),
        toggleValue && (React.createElement("div", { className: cx(classes.row, classes.rowIndented) },
            React.createElement("div", { className: classes.rowText },
                React.createElement("label", { className: classes.rowLabel, htmlFor: 'visitors-auto-promote-switch' }, t('visitors.section.autoPromoteLabel')),
                React.createElement("span", { className: classes.rowHint }, t('visitors.section.autoPromoteDescription')),
                autoPromoteError && (React.createElement("span", { className: cx(classes.notice, classes.noticeError) }, t(autoPromoteError)))),
            React.createElement(Switch, { disabled: autoPromotePending, id: 'visitors-auto-promote-switch', onValueChange: onToggleAutoPromote, value: autoPromoteValue }))),
        toggleValue && (React.createElement("div", { className: cx(classes.row, classes.rowIndented) },
            React.createElement("div", { className: classes.rowText },
                React.createElement("label", { className: classes.rowLabel, htmlFor: 'visitors-raise-hand-disabled-switch' }, t('visitors.section.raiseHandDisabledLabel')),
                React.createElement("span", { className: classes.rowHint }, t('visitors.section.raiseHandDisabledDescription')),
                raiseHandDisabledError && (React.createElement("span", { className: cx(classes.notice, classes.noticeError) }, t(raiseHandDisabledError)))),
            React.createElement(Switch, { disabled: raiseHandDisabledPending, id: 'visitors-raise-hand-disabled-switch', onValueChange: onToggleRaiseHandDisabled, value: raiseHandDisabledValue })))));
};
export default VisitorsSection;
